import React, { ReactNode } from 'react'

import {
  Grid,
  IconButton,
  IconButtonProps,
  InputAdornment,
  OutlinedInput,
  Toolbar,
  ToolbarProps,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, styled, SxProps } from '@mui/material/styles'
import { isFunction } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useDebouncedSearch } from '../../hooks/useDebouncedSearch'
import Iconify from '../Iconify/Iconify'
import SwitchWithTooltip from '../SwitchWithTooltip/SwitchWithTooltip'

// ----------------------------------------------------------------------

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': {
    width: 320,
    boxShadow: `0 8px 16px 0 ${alpha(theme.palette.grey[500], 0.24)}`,
  },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[700]} !important`,
  },
}))

// ----------------------------------------------------------------------

// onDelete: PropTypes.func,
// onDownloadExcel: PropTypes.func,
// onDownloadPdf: PropTypes.func,
// csv: PropTypes.exact({
//   data: PropTypes.array,
//   headers: PropTypes.array,
//   filename: PropTypes.string,
//   canDownloadIfEmptyData: PropTypes.bool,
// }),

type TableToolbarPropsType = ToolbarProps & {
  numSelected?: number
  search?: string
  onSearch?: (value: string) => void
  children?: ReactNode
  searchInputSx?: SxProps
  searchPlaceholder?: string
}

const TableToolbar: React.FC<TableToolbarPropsType> = ({
  numSelected = 0,
  search = '',
  onSearch,
  // onDelete,
  // onDownloadExcel,
  // onDownloadPdf,
  // csv,
  children,
  searchInputSx,
  searchPlaceholder,
  sx,
  variant,
  ...props
}) => {
  const { t } = useTranslation()

  const emptyOnSearch = () => {
    return null
  }

  const { localSearch, handleSearchChange } = useDebouncedSearch(search, onSearch || emptyOnSearch)

  const isDense = variant === 'dense'

  return (
    <Toolbar
      sx={{
        ...(numSelected > 0 && {
          color: 'primary.main',
          bgcolor: 'primary.lighter',
        }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: (theme) => `${theme.spacing(isDense ? 1.5 : 2, 2)} !important`,
        ...sx,
      }}
      variant={variant}
      {...props}
    >
      <Grid container wrap="nowrap">
        <Grid container item xs={5} md={6} spacing={1} alignItems={'center'}>
          {numSelected > 0 ? (
            <Typography component="div" variant="subtitle1">
              {`${numSelected} selected`}
            </Typography>
          ) : isFunction(onSearch) ? (
            <SearchStyle
              size={isDense ? 'small' : 'medium'}
              value={localSearch}
              onChange={handleSearchChange}
              sx={searchInputSx}
              placeholder={searchPlaceholder || t('search.search') || ''}
              startAdornment={
                <InputAdornment position="start">
                  <Iconify
                    icon="eva:search-fill"
                    sx={{ color: 'text.disabled', width: 20, height: 20 }}
                  />
                </InputAdornment>
              }
            />
          ) : null}
        </Grid>

        <Grid
          container
          item
          xs={7}
          md={6}
          spacing={1}
          alignItems={'center'}
          justifyItems={'center'}
          justifyContent={'flex-end'}
        >
          {children}

          {/* {onDownloadPdf && (
            <TableToolbarIconButton
              text={t('button.downloadPDF')}
              onClick={onDownloadPdf}
              icon="bi:filetype-pdf"
            />
          )} */}

          {/* {onDownloadExcel && (
            <TableToolbarIconButton
              text={t('button.download')}
              onClick={onDownloadExcel}
              icon="bi:filetype-xlsx"
            />
          )} */}

          {/* {csv && (
            <TableToolbarItemWrapper>
              <DownloadCsvIconButton
                data={csv.data}
                canDownloadIfEmptyData={csv.canDownloadIfEmptyData}
                headers={csv.headers}
                filename={csv.filename}
              />
            </TableToolbarItemWrapper>
          )} */}

          {/* {numSelected > 0 && (
            <>
              {onDelete && (
                <TableToolbarIconButton
                  text={t('button.delete')}
                  onClick={onDelete}
                  icon="eva:trash-2-fill"
                />
              )}
            </>
          )} */}
        </Grid>
      </Grid>
    </Toolbar>
  )
}

type TableToolbarItemWrapperPropType = {
  children: ReactNode
}

export const TableToolbarItemWrapper: React.FC<TableToolbarItemWrapperPropType> = ({
  children,
}) => (
  <Grid item xs={'auto'} container justifyContent={'center'}>
    {children}
  </Grid>
)

type TableToolbarIconButtonPropTypes = {
  text: string
  onClick: () => void
  color?: IconButtonProps['color']
  icon: string
}

export const TableToolbarIconButton: React.FC<TableToolbarIconButtonPropTypes> = ({
  text,
  onClick,
  color = 'primary',
  icon,
}) => (
  <TableToolbarItemWrapper>
    <Tooltip title={text || ''}>
      <IconButton onClick={onClick} color={color}>
        <Iconify icon={icon} width={25} height={25} />
      </IconButton>
    </Tooltip>
  </TableToolbarItemWrapper>
)

type TableToolbarCheckboxPropType = {
  value: boolean
  text: string
  onChange: () => void
  icon: string
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default'
  iconColor?: string
  sx?: SxProps
}

export const TableToolbarCheckbox: React.FC<TableToolbarCheckboxPropType> = ({
  value,
  text,
  onChange,
  icon,
  color,
  iconColor,
  sx,
}) => (
  <TableToolbarItemWrapper>
    <SwitchWithTooltip
      size={25}
      value={value}
      onChange={onChange}
      text={text}
      icon={icon}
      color={color}
      iconColor={iconColor}
      sx={sx}
    />
  </TableToolbarItemWrapper>
)

export default TableToolbar
